import React, { useContext, useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleSendOutlinedIcon from '@mui/icons-material/ScheduleSendOutlined';
import { Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material';

import useCurrentUser from '~/hooks/useCurrentUser';

import { useLensPinToggle } from '~/pages/AskRichardUnified/useLensPinToggle';

import type { LensResponse } from '~/types/leLens';

import LensCertificationMenuItems, { lensCertificationActions } from '../LensCertificationMenuItems';
import LensCollaborationDialog from '../LensCollaborationDialog';
import SaveLensAsDialog from '../SaveLensAsDialog';
import { SavedViewsContext } from '../personalViews/savedViewsContext';
import LensSchedulesDialog from '../schedules/LensSchedulesDialog';

import type { LensDocumentData, LensState } from './types';

type LensActionsProps = {
  detail: LensResponse;
  data?: LensDocumentData;
  state: LensState;
  onEdit?: () => void;
  asReader?: boolean;
  onRefresh?: () => void;
  refreshing?: boolean;
};

type Dialog = 'copy' | 'schedule' | 'history';

export default function LensActions({ detail, data, state, onEdit, asReader = false, onRefresh, refreshing = false }: LensActionsProps) {
  const { lens, access } = detail;
  const anchor = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const pins = useLensPinToggle();
  const savedViews = useContext(SavedViewsContext);
  const pinned = pins.isPinned(lens.id);
  const open = (next: Dialog) => {
    setMenuOpen(false);
    setDialog(next);
  };
  const run = (action: () => void) => () => {
    setMenuOpen(false);
    action();
  };
  const certification = asReader ? [] : lensCertificationActions(access, detail.certification, detail.sharing?.audience === 'company');
  const editing = Boolean(onEdit) && !asReader;

  return (
    <>
      <Tooltip title="Lens actions">
        <IconButton aria-haspopup="menu" aria-label="Lens actions" onClick={() => setMenuOpen(true)} ref={anchor} size="small">
          <MoreHorizIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor.current} onClose={() => setMenuOpen(false)} open={menuOpen}>
        {editing ? (
          <MenuItem key="edit" onClick={run(onEdit!)}>
            <ListItemIcon>
              <EditOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit this Lens</ListItemText>
            <Typography color="text.secondary" sx={{ ml: 3 }} variant="body2">
              E
            </Typography>
          </MenuItem>
        ) : null}
        {editing ? <Divider key="edit-divider" sx={{ my: 0.5 }} /> : null}
        {savedViews ? (
          <MenuItem key="saved-views" onClick={run(savedViews.open)}>
            <ListItemIcon>
              <BookmarkBorderOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Saved views…
          </MenuItem>
        ) : null}
        <MenuItem onClick={() => open('schedule')}>
          <ListItemIcon>
            <ScheduleSendOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Schedule email…
        </MenuItem>
        <MenuItem onClick={run(() => pins.toggle(lens.id))}>
          <ListItemIcon>{pinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}</ListItemIcon>
          {pinned ? 'Unpin from rail' : 'Pin to rail'}
        </MenuItem>
        <MenuItem onClick={() => open('copy')}>
          <ListItemIcon>
            <ContentCopyOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Duplicate Lens
        </MenuItem>
        <MenuItem onClick={() => open('history')}>
          <ListItemIcon>
            <HistoryOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Version history
        </MenuItem>
        {onRefresh ? (
          <MenuItem disabled={refreshing} onClick={run(onRefresh)}>
            <ListItemIcon>
              <RefreshIcon fontSize="small" />
            </ListItemIcon>
            {refreshing ? 'Refreshing data…' : 'Refresh data'}
          </MenuItem>
        ) : null}
        {certification.length ? <Divider key="certification-divider" sx={{ my: 0.5 }} /> : null}
        <LensCertificationMenuItems
          actions={certification}
          lensId={lens.id}
          meaning={detail.certification?.meaning}
          onDone={() => setMenuOpen(false)}
          parentMenuOpen={menuOpen}
          request={access.canRequestCertification ? detail.certification?.request : undefined}
        />
      </Menu>
      <SaveLensAsDialog
        onClose={() => setDialog(null)}
        onCreated={(created) => {
          setDialog(null);
          navigate(`/ask-richard-unified?view=lens&lens=${encodeURIComponent(created.id)}`);
        }}
        open={dialog === 'copy'}
        sourceLens={lens}
      />
      {dialog === 'schedule' ? (
        <LensSchedulesDialog
          filterOptions={data?.options ?? {}}
          lens={lens}
          onClose={() => setDialog(null)}
          open
          screenValues={state.filters ?? {}}
          userEmail={user?.email}
        />
      ) : null}
      <LensCollaborationDialog
        access={access}
        lens={lens}
        onClose={() => setDialog(null)}
        open={dialog === 'history'}
      />
    </>
  );
}
